/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { styled } from 'styled-components';

import { Reveal, Stagger, StaggerItem } from 'pages/HomeV2/components/Motion';
import {
  Container,
  Eyebrow,
  Lead,
  SectionHead,
  Title,
  v2,
} from 'pages/HomeV2/v2';

import { goodieWorkflow } from '../data-goodie';
import { ICONS } from '../icons';

const DESKTOP = '(min-width: 1024px)';

const Wrapper = styled.section`
  position: relative;
  background: ${v2.bgSoft};
  border-top: 1px solid ${v2.border};
  padding: clamp(72px, 9vw, 130px) 0;
  overflow: hidden;
`;

const Flow = styled.ol`
  position: relative;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;

  /* Vertical connector on mobile ... */
  &::before {
    content: '';
    position: absolute;
    top: 28px;
    bottom: 28px;
    left: 27px;
    width: 2px;
    background: linear-gradient(180deg, ${v2.blue}, ${v2.orange});
    opacity: 0.55;
  }

  /* ... horizontal on desktop. */
  @media ${DESKTOP} {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 12px;
    padding-top: 0;

    &::before {
      top: 28px;
      bottom: auto;
      left: calc(100% / 12);
      right: calc(100% / 12);
      width: auto;
      height: 2px;
      background: linear-gradient(90deg, ${v2.blue}, ${v2.orange});
    }
  }
`;

const StepItem = styled.li`
  position: relative;
  min-width: 0;
`;

const Step = styled(StaggerItem)`
  position: relative;
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  padding: 10px 0;

  @media ${DESKTOP} {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
    gap: 16px;
    padding: 0;
  }
`;

const Node = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid
    ${({ $foxi }) => ($foxi ? 'rgba(255, 159, 79, 0.5)' : v2.borderStrong)};
  background: ${({ $foxi }) =>
    $foxi
      ? 'linear-gradient(160deg, rgba(255, 159, 79, 0.22), rgba(30, 150, 252, 0.16))'
      : v2.bgElevated};
  color: ${({ $foxi }) => ($foxi ? v2.orange : v2.textMuted)};
  box-shadow: ${({ $foxi }) =>
    $foxi
      ? '0 10px 30px rgba(255, 159, 79, 0.18)'
      : '0 10px 30px rgba(0, 0, 0, 0.3)'};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Label = styled.span`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;

  b {
    font-family: ${v2.fontDisplay};
    font-size: 1.02rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.3;
    color: ${v2.text};
  }

  small {
    font-family: ${v2.fontBody};
    font-size: 13.5px;
    line-height: 1.45;
    color: ${v2.textMuted};
  }

  @media ${DESKTOP} {
    b {
      font-size: 0.98rem;
    }
  }
`;

const FoxiTag = styled.span`
  align-self: flex-start;
  margin-top: 4px;
  padding: 3px 8px;
  border-radius: 100px;
  border: 1px solid rgba(255, 159, 79, 0.4);
  background: ${v2.orangeSoft};
  font-family: ${v2.fontBody};
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${v2.orange};

  @media ${DESKTOP} {
    align-self: center;
  }
`;

const Legend = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  max-width: 520px;
  margin: 40px auto 0;
  font-family: ${v2.fontBody};
  font-size: 13.5px;
  line-height: 1.5;
  color: ${v2.textFaint};
  text-align: left;

  i {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${v2.orange};
    box-shadow: 0 0 10px ${v2.orange};
    flex-shrink: 0;
  }
`;

const GoodieWorkflow = () => (
  <Wrapper id="workflow" aria-labelledby="goodie-workflow-title">
    <Container>
      <SectionHead>
        <Reveal>
          <Eyebrow>Ablauf</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <Title id="goodie-workflow-title">
            So passt FOXI in den <span className="grad">Praxisalltag</span>
          </Title>
        </Reveal>
        <Reveal delay={0.2}>
          <Lead>
            Vom ersten Kontakt bis zur Erinnerung an den nächsten Termin – FOXI
            begleitet die Schritte, die sonst Zeit am Empfang kosten.
          </Lead>
        </Reveal>
      </SectionHead>

      <Stagger gap={0.1}>
        <Flow>
          {goodieWorkflow.map((step) => {
            const Icon = ICONS[step.icon];
            return (
              <StepItem key={step.title}>
                <Step y={20}>
                  <Node $foxi={step.foxi} aria-hidden>
                    <Icon />
                  </Node>
                  <Label>
                    <b>{step.title}</b>
                    <small>{step.hint}</small>
                    {step.foxi && <FoxiTag>mit FOXI</FoxiTag>}
                  </Label>
                </Step>
              </StepItem>
            );
          })}
        </Flow>
      </Stagger>

      <Reveal delay={0.2}>
        <Legend>
          <i aria-hidden />
          Orange markierte Schritte übernimmt FOXI für Ihr Team.
        </Legend>
      </Reveal>
    </Container>
  </Wrapper>
);

export default GoodieWorkflow;
